import { RuntimeCatalog } from "./schema"
import { canonicalJson, resolveCatalog } from "./resolve"
import { assertValidContent, safeContentPath } from "./validate"

/** Bump when the engine can no longer interpret a previously supported content contract. */
export const ENGINE_CONTENT_VERSION = 1

export interface ContentFileDigest { sha256: string; size: number }
export interface ContentReleaseIntegrity {
    catalog: ContentFileDigest
    media: Record<string, ContentFileDigest>
    server: Record<string, ContentFileDigest>
}

export interface ContentReleaseManifest {
    schemaVersion: 1
    engineContentVersion: number
    revision: string
    catalog: "catalog.json"
    media: string[]
    /** Absent only for legacy bundled catalogs. Live installed releases require this inventory. */
    integrity?: ContentReleaseIntegrity
    /**
     * The image each `X.json` atlas draws -- authored-terrain.json draws authored-terrain-v2.png, so
     * no name says it. (An `X.png.json` atlas must draw X.png; the compiler refuses anything else.)
     * A client restaging a changed image restages the atlases that draw it, or it keeps the old art.
     */
    atlasImages?: Record<string, string>
}

const record = (value: unknown): value is Record<string, any> =>
    !!value && typeof value === "object" && !Array.isArray(value)
const revisionPattern = /^[0-9a-f]{64}$/

/** A running game always has at least its installed room/world baseline. */
export function assertRequiredInstalledReleases(value: unknown): asserts value is string[] {
    if (!Array.isArray(value) || !value.length || value.some(id => typeof id !== "string" || !revisionPattern.test(id)))
        throw new Error("The local server must report valid installed release dependencies before live compilation.")
}

/**
 * A release serves each file from `content/<revision>/<file>`: its own revision, or -- for a
 * release held only in a server's memory -- the installed release whose identical copy the web
 * host already serves. Never another file, host or path.
 */
export const releaseAssetPath = (published: unknown, file: string): boolean => {
    if (typeof published !== "string" || !published.startsWith("content/")) return false
    const rest = published.slice("content/".length), slash = rest.indexOf("/")
    return slash === 64 && revisionPattern.test(rest.slice(0, 64)) && rest.slice(65) === file
}
/** The revision a release asset path is served from. */
export const releaseAssetRevision = (published: string): string => published.slice("content/".length, "content/".length + 64)
export const RELEASE_METADATA_FILES = ["manifest.json", "catalog.json"] as const

export const createContentReleaseManifest = (revision: string, media: string[], integrity?: ContentReleaseIntegrity, atlasImages?: Record<string, string>): ContentReleaseManifest => {
    const manifest: ContentReleaseManifest = { schemaVersion: 1, engineContentVersion: ENGINE_CONTENT_VERSION, revision, catalog: "catalog.json", media: [...media], ...(integrity ? { integrity } : {}), ...(atlasImages ? { atlasImages } : {}) }
    assertContentReleaseManifest(manifest)
    return manifest
}

export function assertContentReleaseManifest(value: unknown): asserts value is ContentReleaseManifest {
    if (!record(value) || value.schemaVersion !== 1 || value.engineContentVersion !== ENGINE_CONTENT_VERSION)
        throw new Error("Incompatible content release: unsupported schema or engine content version")
    if (!revisionPattern.test(value.revision) || value.catalog !== "catalog.json") throw new Error("Invalid content release identity")
    if (!Array.isArray(value.media) || value.media.some(file => !safeContentPath(file) || (RELEASE_METADATA_FILES as readonly string[]).includes(file)) || new Set(value.media).size !== value.media.length)
        throw new Error("Invalid content release media paths (duplicate, unsafe or reserved)")
    if (value.integrity !== undefined) {
        const digest = (entry: any) => record(entry) && typeof entry.sha256 === "string" && revisionPattern.test(entry.sha256) && Number.isSafeInteger(entry.size) && entry.size >= 0
        const inventory = (entries: any) => record(entries) && Object.entries(entries).every(([file, entry]) => safeContentPath(file) && digest(entry))
        const integrity = value.integrity
        if (!record(integrity) || !digest(integrity.catalog) || !inventory(integrity.media) || !inventory(integrity.server) ||
            canonicalJson(Object.keys(integrity.media).sort()) !== canonicalJson([...value.media].sort()) || !integrity.server["index.json"])
            throw new Error("Invalid content release integrity inventory")
    }
    if (value.atlasImages !== undefined) {
        const media = new Set(value.media)
        if (!record(value.atlasImages) || Object.entries(value.atlasImages).some(([atlas, image]) => !media.has(atlas) || typeof image !== "string" || !media.has(image)))
            throw new Error("Invalid content release atlas images")
    }
}

/** Validate downloaded tables against their source rather than trusting derived gameplay data. */
export function assertRuntimeContentRelease(manifest: unknown, value: unknown): asserts value is RuntimeCatalog {
    assertContentReleaseManifest(manifest)
    if (!record(value) || value.schemaVersion !== 1 || value.revision !== manifest.revision) throw new Error("Content catalog does not match its release")
    assertValidContent(value.source)
    const derived = resolveCatalog(value.source, value.revision)
    for (const key of ["items", "spriteColors", "tiles", "npcDrops", "itemDrops", "creatures", "weapons"] as const) {
        if (canonicalJson(value[key]) !== canonicalJson(derived[key])) throw new Error(`Invalid derived content table: ${key}`)
    }
    if (!record(value.assets) || Object.keys(value.assets).length !== manifest.media.length) throw new Error("Content asset index does not match release media")
    for (const file of manifest.media) {
        if (!releaseAssetPath(value.assets[file], file)) throw new Error(`Invalid release asset path: ${file}`)
    }
    // Full geometry/atlas validation belongs to the compiler. The release must at least contain
    // the registered map tables; these are not derivable from source catalogs alone.
    for (const [key, entries] of [["mapTemplates", value.source.maps.templates], ["mapTilesets", value.source.maps.tilesets]] as const) {
        if (!record(value[key]) || canonicalJson(Object.keys(value[key]).sort()) !== canonicalJson(entries.map(entry => entry.id).sort()))
            throw new Error(`Invalid release map index: ${key}`)
        if (Object.values(value[key]).some(entry => !record(entry))) throw new Error(`Invalid release map document: ${key}`)
    }
}
